import { FormEvent, SyntheticEvent, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ButtonLoading from "../../../Button/ButtonLoading";
import SearchBar from "../../../Algolia/SearchBar";
import CaseHit from "../../../Case/CaseEntityPicker/Hit/CaseHit";
import { CaseHit as CaseHitType } from "../../../Case/CaseEntityPicker/Hit/hitType";
import ContentBox from "../../../ContentBox/ContentBox";
import LegalbirdIoModal from "../../../Modal/LegalbirdIoModal";
import PagePagination from "../../../PagePagination/PagePagination";
import DisplayMediaObjectContent from "../../../MediaObjectSection/DisplayMediaObjectContent";
import { ALGOLIA_INDEX } from "../../../../config/_entrypoint";
import { queryKeys } from "../../../../services/ReactQuery/reactQueryService";
import {
  fetchMediaObjectForTextComparison,
  fetchMediaObjectsWithTextResult,
  fetchMediaObjectTextResult,
  MEDIA_OBJECT_TO_TEXT_RESULTS_PER_PAGE,
} from "../../../../services/mediaObjectToTextQualityAssuranceService";
import { MediaObject } from "../../../../types/MediaObject";
import aiLabMediaObjectToTextQualityAssuranceStyles from "./aiLabMediaObjectToTextQualityAssuranceStyles";

type MediaObjectTextResultModalProps = {
  mediaObject: MediaObject;
  handleClose: () => void;
};

const MediaObjectTextResultModal = ({ mediaObject, handleClose }: MediaObjectTextResultModalProps) => {
  const {
    data: textResult,
    isPending,
    isError,
  } = useQuery({
    queryKey: [...queryKeys.items("/lb/media_objects"), mediaObject.id, "markdown"],
    queryFn: ({ signal }) => fetchMediaObjectTextResult(mediaObject.id, signal),
  });

  return (
    <LegalbirdIoModal
      handleClose={handleClose}
      open
      title={`Fall ${mediaObject.productId}: ${mediaObject.description || mediaObject.originalName}`}
      hasActions={false}
      fullWidth
      maxWidth={false}
    >
      <Grid container spacing={2}>
        <Grid size={6} sx={aiLabMediaObjectToTextQualityAssuranceStyles.modalColumn}>
          <Grid container spacing={1}>
            <Grid size={12}>
              <Typography variant="h6">Dokument</Typography>
            </Grid>
            <Grid size={12}>
              <Box sx={aiLabMediaObjectToTextQualityAssuranceStyles.documentContainer}>
                <DisplayMediaObjectContent mediaObject={mediaObject} mediaRotation={0} />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={6} sx={aiLabMediaObjectToTextQualityAssuranceStyles.modalColumn}>
          <Grid container spacing={1}>
            <Grid size={12}>
              <Typography variant="h6">Markdown-Ergebnis</Typography>
            </Grid>
            <Grid size={12}>
              <Box sx={aiLabMediaObjectToTextQualityAssuranceStyles.resultContainer}>
                {isPending && (
                  <Grid container spacing={2} sx={aiLabMediaObjectToTextQualityAssuranceStyles.resultState}>
                    <Grid size="auto">
                      <CircularProgress size={24} />
                    </Grid>
                    <Grid size="auto">
                      <Typography>Ergebnis wird geladen.</Typography>
                    </Grid>
                  </Grid>
                )}
                {isError && (
                  <Grid container sx={aiLabMediaObjectToTextQualityAssuranceStyles.resultState}>
                    <Grid size="auto">
                      <Typography color="error">Das Ergebnis konnte nicht geladen werden.</Typography>
                    </Grid>
                  </Grid>
                )}
                {!isPending && !isError && !textResult && (
                  <Grid container sx={aiLabMediaObjectToTextQualityAssuranceStyles.resultState}>
                    <Grid size="auto">
                      <Typography>Kein Ergebnis vorhanden.</Typography>
                    </Grid>
                  </Grid>
                )}
                {!isPending && !isError && textResult && (
                  <Box component="pre" sx={aiLabMediaObjectToTextQualityAssuranceStyles.markdownText}>
                    {textResult}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </LegalbirdIoModal>
  );
};

export default function AiLabMediaObjectToTextQualityAssurance() {
  const [page, setPage] = useState(1);
  const [caseFilter, setCaseFilter] = useState<Pick<MediaObject, "product" | "productId"> | null>(null);
  const [directMediaObjectId, setDirectMediaObjectId] = useState("");
  const [directMediaObjectError, setDirectMediaObjectError] = useState("");
  const [isDirectMediaObjectLoading, setIsDirectMediaObjectLoading] = useState(false);
  const [selectedMediaObject, setSelectedMediaObject] = useState<MediaObject | null>(null);
  const {
    data: mediaObjectCollection,
    isPending,
    isError,
  } = useQuery({
    queryKey: [
      ...queryKeys.collections("media_objects"),
      "withTextResult",
      caseFilter?.product ?? null,
      caseFilter?.productId ?? null,
      page,
    ],
    queryFn: ({ signal }) => fetchMediaObjectsWithTextResult(page, caseFilter, signal),
  });

  const mediaObjects = mediaObjectCollection?.["hydra:member"] ?? [];
  const totalPages = Math.ceil(
    (mediaObjectCollection?.["hydra:totalItems"] ?? 0) / MEDIA_OBJECT_TO_TEXT_RESULTS_PER_PAGE,
  );

  const selectCase = ({ product, productId }: CaseHitType, event?: SyntheticEvent<HTMLElement>) => {
    if (!event) {
      return;
    }

    event.preventDefault();
    setCaseFilter({ product, productId });
    setPage(1);
  };

  const clearCaseFilter = () => {
    setCaseFilter(null);
    setPage(1);
  };

  const openMediaObjectById = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const mediaObjectId = Number(directMediaObjectId);

    if (!Number.isInteger(mediaObjectId) || mediaObjectId < 1) {
      setDirectMediaObjectError("Bitte eine gültige MediaObject-ID angeben.");
      return;
    }

    setDirectMediaObjectError("");
    setIsDirectMediaObjectLoading(true);
    try {
      setSelectedMediaObject(await fetchMediaObjectForTextComparison(mediaObjectId));
    } catch {
      setDirectMediaObjectError("Das MediaObject konnte nicht geladen werden.");
    } finally {
      setIsDirectMediaObjectLoading(false);
    }
  };

  return (
    <>
      <ContentBox headline="MediaObject-to-Text-Qualitätssicherung">
        <Grid container spacing={2} sx={aiLabMediaObjectToTextQualityAssuranceStyles.controlsContainer}>
          <Grid size={6}>
            <Grid container spacing={1}>
              <Grid size={12}>
                <Typography variant="subtitle1">Nach Fall filtern</Typography>
              </Grid>
              <Grid size={12}>
                <SearchBar
                  searchConfig={{ indexName: ALGOLIA_INDEX }}
                  resultListConfig={{ itemSize: 330, width: "100%" }}
                  HitComponent={CaseHit}
                  onHitClick={selectCase}
                  placeHolder="FALL SUCHEN"
                />
              </Grid>
              <Grid size={12}>
                <Grid container spacing={1} sx={aiLabMediaObjectToTextQualityAssuranceStyles.caseFilterStatus}>
                  <Grid size="auto">
                    <Typography>
                      {caseFilter
                        ? `Fall-ID ${caseFilter.productId} (${caseFilter.product})`
                        : "MediaObjects aller Fälle"}
                    </Typography>
                  </Grid>
                  {caseFilter && (
                    <Grid size="auto">
                      <Button size="small" variant="outlined" onClick={clearCaseFilter}>
                        Filter entfernen
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={6}>
            <Grid container component="form" spacing={1} onSubmit={openMediaObjectById}>
              <Grid size={12}>
                <Typography variant="subtitle1">Vergleich direkt öffnen</Typography>
              </Grid>
              <Grid size={12}>
                <Grid container spacing={1} sx={aiLabMediaObjectToTextQualityAssuranceStyles.directIdControls}>
                  <Grid size="grow">
                    <TextField
                      fullWidth
                      label="MediaObject-ID"
                      type="number"
                      value={directMediaObjectId}
                      onChange={({ target }) => {
                        setDirectMediaObjectId(target.value);
                        setDirectMediaObjectError("");
                      }}
                      error={Boolean(directMediaObjectError)}
                      helperText={directMediaObjectError}
                      disabled={isDirectMediaObjectLoading}
                      slotProps={{ htmlInput: { min: 1, step: 1 } }}
                    />
                  </Grid>
                  <Grid size="auto">
                    <ButtonLoading
                      type="submit"
                      variant="contained"
                      isLoading={isDirectMediaObjectLoading}
                      disabled={!directMediaObjectId}
                      sx={aiLabMediaObjectToTextQualityAssuranceStyles.directIdButton}
                    >
                      Vergleich öffnen
                    </ButtonLoading>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fall-ID</TableCell>
                <TableCell>Dokumentbeschreibung</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending && (
                <TableRow>
                  <TableCell colSpan={2} sx={aiLabMediaObjectToTextQualityAssuranceStyles.centeredCell}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
              {isError && (
                <TableRow>
                  <TableCell colSpan={2} sx={aiLabMediaObjectToTextQualityAssuranceStyles.centeredCell}>
                    <Typography color="error">MediaObjects konnten nicht geladen werden.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!isPending && !isError && mediaObjects.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} sx={aiLabMediaObjectToTextQualityAssuranceStyles.centeredCell}>
                    {caseFilter
                      ? "Keine MediaObjects mit Text-Ergebnis für diesen Fall vorhanden."
                      : "Keine MediaObjects mit Text-Ergebnis vorhanden."}
                  </TableCell>
                </TableRow>
              )}
              {mediaObjects.map((mediaObject) => (
                <TableRow
                  hover
                  key={mediaObject.id}
                  onClick={() => setSelectedMediaObject(mediaObject)}
                  sx={aiLabMediaObjectToTextQualityAssuranceStyles.clickableRow}
                >
                  <TableCell>{mediaObject.productId}</TableCell>
                  <TableCell>{mediaObject.description || "–"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Grid container sx={aiLabMediaObjectToTextQualityAssuranceStyles.paginationContainer}>
            <Grid size="auto">
              <PagePagination
                currentPage={page}
                totalPages={totalPages}
                handlePrevPage={() => setPage((currentPage) => currentPage - 1)}
                handleNextPage={() => setPage((currentPage) => currentPage + 1)}
              />
            </Grid>
          </Grid>
        )}
      </ContentBox>
      {selectedMediaObject && (
        <MediaObjectTextResultModal
          mediaObject={selectedMediaObject}
          handleClose={() => setSelectedMediaObject(null)}
        />
      )}
    </>
  );
}
